// Multi-select category filtering (:plp_multi_select_categories) over the flat
// `category_paths` facet: every value is a full hierarchy path and each listing
// carries all its ancestor paths. Pure logic only — rendering lives in
// src/algolia_search/categoryTreeFilter.js, URL handling in searchRouting.js.

import { isRoomTypePage, isSeoLocationRoute } from '../urlHelpers'
import { stripLocaleFromPath } from '../localeHelpers'
import { flipperFlagEnabled, getTable } from './helpers'
import { categoryHierarchyUpToModifier, categoryNamesFromRoutes, getModifierFromLocation } from './seoRouting'

export function multiSelectCategoriesEnabled () {
  return flipperFlagEnabled('plp-multi-select-categories')
}

function pathSegments (path) {
  return path.split(' > ')
}

// 'Furniture > Seating > Armchairs' -> 'Armchairs'
export function pathLeaf (path) {
  return pathSegments(path).pop()
}

// Checkboxes exist at product-type depth and below; category and subcategory
// rows are expand-only scope
export function hasCheckbox (path) {
  return pathSegments(path).length >= 3
}

export function isAncestorOf (candidate, path) {
  return path.startsWith(candidate + ' > ')
}

// 'Furniture > Seating > Armchairs' -> 'furniture/seating/armchairs'
export function hierarchyToPath (value) {
  return pathSegments(value).join('/')
    .split(' ').join('-')
    .toLowerCase()
}

// Ticking removes ancestors (child overrides parent), descendants (parent
// swallows children) and any expand-only scope value — the landing scope is a
// default that an explicit tick replaces, even across branches. Unticking
// simply removes the path.
export function selectionAfterToggle (selectedPaths, toggledPath) {
  if (selectedPaths.includes(toggledPath)) {
    return selectedPaths.filter(path => path !== toggledPath)
  }

  return selectedPaths
    .filter(path => hasCheckbox(path))
    .filter(path => !isAncestorOf(path, toggledPath) && !isAncestorOf(toggledPath, path))
    .concat([toggledPath])
}

// The category path implied by the current URL ('Furniture > Seating' on
// /furniture/seating); null on non-category routes (/search, sellers…)
export function pageTrailPath (location) {
  if (!isSeoLocationRoute(location)) return null

  const strippedPathname = stripLocaleFromPath(location.pathname)
  const allSegments = strippedPathname.split('/').filter(String)
  const routeSegments = isRoomTypePage(strippedPathname) ? allSegments.slice(2) : allSegments
  const [modifierIndex] = getModifierFromLocation(routeSegments, getTable('modifiers'))
  const categoryNames = categoryNamesFromRoutes(routeSegments)
  const trailSegments = categoryHierarchyUpToModifier(categoryNames, modifierIndex)

  if (trailSegments.length === 0) return null
  return trailSegments.join(' > ')
}

// Where the selection lands when the last tick is removed on a category page:
// the page's own scope, climbed to the nearest expand-only depth so it renders
// as scope rather than a phantom tick
export function landingFallbackPath (location) {
  const trail = pageTrailPath(location)
  if (!trail) return null

  return nearestScopePath(trail)
}

function nearestScopePath (path) {
  if (!hasCheckbox(path)) return path
  return nearestScopePath(pathSegments(path).slice(0, -1).join(' > '))
}

// Flat facet values -> tree of { path, label, count, isRefined, refinedInBranch,
// children }. Ancestors missing from the facet response are synthesised with a
// null count; children sort refined-branch first, then count descending.
export function buildCategoryTree (items) {
  const nodesByPath = new Map()
  const roots = []

  items.forEach(item => {
    const node = findOrCreateNode(nodesByPath, roots, item.value)
    node.count = item.count
    node.isRefined = item.isRefined
  })

  roots.forEach(root => markRefinedBranches(root))
  roots.forEach(root => sortBranch(root))
  return sortedSiblings(roots)
}

function findOrCreateNode (nodesByPath, roots, path) {
  if (nodesByPath.has(path)) return nodesByPath.get(path)

  const segments = pathSegments(path)
  const node = {
    path,
    label: segments[segments.length - 1],
    count: null,
    isRefined: false,
    refinedInBranch: false,
    children: []
  }
  nodesByPath.set(path, node)

  if (segments.length > 1) {
    findOrCreateNode(nodesByPath, roots, segments.slice(0, -1).join(' > ')).children.push(node)
  } else {
    roots.push(node)
  }
  return node
}

function markRefinedBranches (node) {
  const childRefined = node.children.map(child => markRefinedBranches(child)).some(Boolean)
  node.refinedInBranch = node.isRefined || childRefined
  return node.refinedInBranch
}

function sortBranch (node) {
  node.children = sortedSiblings(node.children)
  node.children.forEach(child => sortBranch(child))
}

function sortedSiblings (nodes) {
  return [...nodes].sort((a, b) => {
    if (a.refinedInBranch !== b.refinedInBranch) return a.refinedInBranch ? -1 : 1
    return (b.count || 0) - (a.count || 0)
  })
}

// For currentRefinements widgets (pills, filter count): the landing scope is
// the page itself, so only real ticks are dismissible or counted
export function withoutLandingScopeCategoryRefinements (items) {
  return items
    .map(item => {
      if (item.attribute !== 'category_paths') return item
      return { ...item, refinements: item.refinements.filter(refinement => hasCheckbox(refinement.value)) }
    })
    .filter(item => item.refinements.length > 0)
}
